import React, { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { StatusPill } from "@/components/billing/PlanCard";
import TransactionsList from "@/components/billing/TransactionsList";
import UsageHistory from "@/components/billing/UsageHistory";
import UserBillingActions from "./UserBillingActions";
import RefundDialog from "./RefundDialog";
import { fmtCAD, fmtDate, fmtInt } from "@/lib/billing";

export default function UserBillingDrawer({ user, open, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [refundTxn, setRefundTxn] = useState(null);
  // Hold onto the last non-null user so the Sheet can finish its exit
  // animation after the parent clears billingUser (otherwise children
  // like UserBillingActions read user.email on null and crash the page).
  const [cachedUser, setCachedUser] = useState(user);
  useEffect(() => {
    if (user) setCachedUser(user);
  }, [user]);
  const effUser = user || cachedUser;

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data: res } = await api.get(`/admin/billing/users/${user.id}`);
      setData(res);
      setErr("");
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [user]);

  useEffect(() => {
    if (open) { setData(null); load(); }
  }, [open, load]);

  const st = data?.status;
  const c = data?.credits;
  const u = data?.usage;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="user-billing-drawer">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display">Billing — {effUser?.email}</SheetTitle>
          <SheetDescription>Subscription, credits, manual grants and refunds for this member.</SheetDescription>
        </SheetHeader>

        {err ? (
          <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="user-billing-error">{err}</div>
        ) : !data ? (
          <div className="mt-6 flex items-center gap-2 text-sm text-slate-500" data-testid="user-billing-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="mt-5 space-y-6">
            <section className="rounded-md border border-slate-200 p-3" data-testid="user-billing-status">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-slate-900">Subscription</h3>
                <StatusPill status={st.status} testid="user-billing-status-pill" />
              </div>
              <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <Info label={st.status === "cancelling" ? "Access until" : "Period end"} value={st.period_end ? fmtDate(st.period_end) : (st.complimentary_until ? `${fmtDate(st.complimentary_until)} (comp.)` : "—")} testid="user-billing-period-end" />
                <Info label="Card on file" value={st.has_card ? `${st.card_brand || "Card"} •••• ${st.card_last4 || "????"}` : "None"} testid="user-billing-card" />
                <Info label="Province" value={st.billing_province || "Not set"} testid="user-billing-province" />
                <Info label="Granted until" value={st.granted_until ? fmtDate(st.granted_until) : "—"} testid="user-billing-granted-until" />
              </dl>
              {st.last_failure ? <p className="mt-2 text-xs text-amber-800" data-testid="user-billing-last-failure">Last failure: {st.last_failure} · retries: {st.retry_count}</p> : null}
            </section>

            <section className="grid grid-cols-2 gap-3" data-testid="user-billing-credits">
              {["enrichment", "scrape"].map((pool) => (
                <div key={pool} className="rounded-md border border-slate-200 p-3" data-testid={`user-billing-credits-${pool}`}>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{pool} credits</div>
                  <div className="text-2xl font-bold text-slate-900 tabular-nums font-display mt-1" data-testid={`user-billing-credits-${pool}-total`}>{fmtInt(c[pool]?.total)}</div>
                  <div className="text-[11px] text-slate-500">{fmtInt(c[pool]?.included)} included · {fmtInt(c[pool]?.purchased)} purchased</div>
                </div>
              ))}
            </section>

            <section className="rounded-md border border-slate-200 p-3 text-sm" data-testid="user-billing-usage">
              <h3 className="text-sm font-semibold text-slate-900">Usage &amp; provider cost</h3>
              <dl className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Info label="Realtors added" value={fmtInt(u.realtors_added)} />
                <Info label="Realtors enriched" value={fmtInt(u.realtors_enriched)} />
                <Info label="Listings found" value={fmtInt(u.listings_found)} />
                <Info label="Provider cost" value={`${fmtCAD(u.provider_cost_cad)} (US$${u.provider_cost_usd})`} testid="user-billing-provider-cost" />
              </dl>
              {Object.keys(u.credits_by_feature || {}).length ? (
                <p className="mt-2 text-[11px] text-slate-500">
                  Credits by feature: {Object.entries(u.credits_by_feature).map(([k, v]) => `${k} ${fmtInt(v)}`).join(" · ")}
                </p>
              ) : null}
            </section>

            <UserBillingActions user={effUser} data={data} onChanged={load} />

            <section data-testid="user-billing-transactions">
              <h3 className="text-sm font-semibold text-slate-900">Transactions &amp; receipts</h3>
              <TransactionsList rows={data.transactions} compact onRefund={setRefundTxn} testid="user-billing-transactions-list" />
            </section>

            <section data-testid="user-billing-ledger">
              <h3 className="text-sm font-semibold text-slate-900">Credit ledger</h3>
              <UsageHistory rows={data.ledger} compact testid="user-billing-ledger-table" />
            </section>
          </div>
        )}

        <RefundDialog
          txn={refundTxn}
          userId={effUser?.id}
          onClose={() => setRefundTxn(null)}
          onDone={() => { setRefundTxn(null); load(); toast.success("Refund recorded"); }}
        />
      </SheetContent>
    </Sheet>
  );
}

function Info({ label, value, testid }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="text-sm font-medium text-slate-900 mt-0.5" data-testid={testid}>{value}</dd>
    </div>
  );
}
